import { FC, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import useTranslation from 'next-translate/useTranslation'
import TrendingAiSearchCard, {
  TrendingAiSearchCardVariant
} from './trendingAiSearchCard'
import AiSearchSectionHeader from './aiSearchSectionHeader'

type TrendingAiSearch = {
  label: string
  imageUrl: string
  variant: TrendingAiSearchCardVariant
}

const DEFAULT_TRENDING_VARIANTS: TrendingAiSearchCardVariant[] = [
  'green',
  'blue',
  'red',
  'purple'
]

const TRENDING_IMAGE_BASE_URL = 'https://i.factcool.com/aiSearch'

const DRAG_CLICK_THRESHOLD = 5

export const useDefaultTrendingSearches = (): TrendingAiSearch[] => {
  const { t } = useTranslation('common')

  return useMemo<TrendingAiSearch[]>(
    () =>
      DEFAULT_TRENDING_VARIANTS.map((variant, index) => {
        const position = index + 1

        return {
          label: t(`Toolbar.AiSearch.trendingSearch${position}`),
          imageUrl: `${TRENDING_IMAGE_BASE_URL}/trendingSearch${position}.webp`,
          variant
        }
      }),
    [t]
  )
}

interface TrendingAiSearchesProps {
  searches?: TrendingAiSearch[]
  onSelect?: (label: string) => void
  onShowAll?: () => void
}

const TrendingAiSearches: FC<TrendingAiSearchesProps> = ({
  searches,
  onSelect,
  onShowAll
}) => {
  const { t } = useTranslation('common')
  const defaultSearches = useDefaultTrendingSearches()

  const items = searches ?? defaultSearches

  const cardsRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const drag = useRef({
    isPressed: false,
    isDragging: false,
    startX: 0,
    startScrollLeft: 0,
    distance: 0
  })

  useEffect(() => {
    const cards = cardsRef.current

    if (!cards) {
      return
    }

    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
        return
      }

      const maxScrollLeft = cards.scrollWidth - cards.clientWidth

      if (maxScrollLeft <= 0) {
        return
      }

      const atStart = event.deltaY < 0 && cards.scrollLeft <= 0
      const atEnd = event.deltaY > 0 && cards.scrollLeft >= maxScrollLeft

      if (atStart || atEnd) {
        return
      }

      event.preventDefault()
      cards.scrollLeft = Math.min(
        maxScrollLeft,
        Math.max(0, cards.scrollLeft + event.deltaY)
      )
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) {
        return
      }

      if (cards.scrollWidth - cards.clientWidth <= 0) {
        return
      }

      drag.current = {
        isPressed: true,
        isDragging: false,
        startX: event.clientX,
        startScrollLeft: cards.scrollLeft,
        distance: 0
      }
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (!drag.current.isPressed) {
        return
      }

      if (event.buttons === 0) {
        drag.current.isPressed = false

        return
      }

      const delta = event.clientX - drag.current.startX

      drag.current.distance = Math.max(drag.current.distance, Math.abs(delta))

      if (!drag.current.isDragging) {
        if (drag.current.distance <= DRAG_CLICK_THRESHOLD) {
          return
        }

        drag.current.isDragging = true
        setIsDragging(true)
        cards.setPointerCapture(event.pointerId)
      }

      cards.scrollLeft = drag.current.startScrollLeft - delta
    }

    const handlePointerUp = (event: PointerEvent) => {
      if (!drag.current.isPressed) {
        return
      }

      drag.current.isPressed = false

      if (!drag.current.isDragging) {
        return
      }

      drag.current.isDragging = false
      setIsDragging(false)

      if (cards.hasPointerCapture(event.pointerId)) {
        cards.releasePointerCapture(event.pointerId)
      }
    }

    const handleClickCapture = (event: MouseEvent) => {
      if (drag.current.distance <= DRAG_CLICK_THRESHOLD) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      drag.current.distance = 0
    }

    cards.addEventListener('wheel', handleWheel, { passive: false })
    cards.addEventListener('pointerdown', handlePointerDown)
    cards.addEventListener('pointermove', handlePointerMove)
    cards.addEventListener('pointerup', handlePointerUp)
    cards.addEventListener('pointercancel', handlePointerUp)
    cards.addEventListener('click', handleClickCapture, true)

    return () => {
      cards.removeEventListener('wheel', handleWheel)
      cards.removeEventListener('pointerdown', handlePointerDown)
      cards.removeEventListener('pointermove', handlePointerMove)
      cards.removeEventListener('pointerup', handlePointerUp)
      cards.removeEventListener('pointercancel', handlePointerUp)
      cards.removeEventListener('click', handleClickCapture, true)
    }
  }, [items])

  if (!items.length) {
    return null
  }

  return (
    <StyledContainer>
      <AiSearchSectionHeader
        heading={t('Toolbar.AiSearch.trendingTitle')}
        onShowAll={onShowAll}
      />
      <StyledCards ref={cardsRef} isDragging={isDragging}>
        {items.map((search) => (
          <TrendingAiSearchCard
            key={search.label}
            label={search.label}
            imageUrl={search.imageUrl}
            variant={search.variant}
            onClick={onSelect}
          />
        ))}
      </StyledCards>
    </StyledContainer>
  )
}

// export default TrendingAiSearches

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StyledCards = styled.div<{ isDragging?: boolean }>`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (hover: hover) and (pointer: fine) {
    padding-bottom: 10px;
    user-select: none;
    cursor: ${({ isDragging }) => (isDragging ? 'grabbing' : 'grab')};

    &::-webkit-scrollbar {
      display: block;
      height: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: #e0e0e0;
      border-radius: 3px;
    }
  }
`
